import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getPlatformLogoSrc } from "../../components/Reports/Components/utils";
import { numberFormatter } from "../../helper/Reports/common";
import { useAmazonPerformanceSummary } from "./dashboardHooks";
import SectionInfoIcon from "./SectionInfoIcon";
import imageThumbnail from "../../assets/images/image_thumbnail.svg";

const userPlatform =
  JSON.parse(localStorage.getItem("user_platform")) || "amazon";

const METRIC_COLUMNS = [
  {
    key: "spends",
    title: "Spends",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  { key: "orders", title: "Orders", maximumFractionDigits: 0 },
  {
    key: "revenue",
    title: "Revenue",
    isCurrencyFormat: true,
    maximumFractionDigits: 0,
  },
  { key: "roas", title: "ROAS", maximumFractionDigits: 2 },
];

const HEADER_CELL_SX = {
  borderBottom: "1px solid #E0E0E0",
  padding: "8px 12px",
  fontSize: "12px",
  color: "text.secondary",
  fontWeight: 600,
  bgcolor: "rgb(234, 230, 253)",
};

const BODY_CELL_SX = {
  padding: "12px",
  fontSize: "12px",
};

function MetricHeader({ title }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-end",
        flexDirection: "column",
        justifyContent: "flex-end",
        gap: 0.5,
      }}
    >
      <img
        src={getPlatformLogoSrc(userPlatform)}
        alt={userPlatform}
        style={{ width: "14px", height: "14px" }}
      />
      <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>{title}</Typography>
    </Box>
  );
}

function formatMetric(column, value) {
  if (value === null || value === undefined || value === "") return "-";
  return numberFormatter(
    value,
    !!column.isCurrencyFormat,
    "number",
    column.maximumFractionDigits
  );
}

function PerformanceTable({
  firstColumnTitle,
  rows,
  loading,
  showProductImage = false,
  emptyMessage,
}) {
  return (
    <Paper
      sx={{
        borderRadius: 2,
        boxShadow: "none",
        border: "1px solid #E0E0E0",
        overflow: "hidden",
        flex: 1,
      }}
    >
      <TableContainer>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ ...HEADER_CELL_SX, width: "36%" }}>
                <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>
                  {firstColumnTitle}
                </Typography>
              </TableCell>
              {METRIC_COLUMNS.map((column) => (
                <TableCell key={column.key} align="right" sx={HEADER_CELL_SX}>
                  <MetricHeader title={column.title} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? [...Array(5)].map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`} sx={{ height: "58px" }}>
                    <TableCell
                      sx={{
                        ...BODY_CELL_SX,
                        borderBottom:
                          rowIndex === 4 ? "none" : "1px solid #E0E0E0",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {showProductImage ? (
                          <Skeleton
                            variant="rectangular"
                            width={32}
                            height={32}
                            sx={{ borderRadius: "2px", flexShrink: 0 }}
                          />
                        ) : null}
                        <Skeleton width="80%" height={20} />
                      </Box>
                    </TableCell>
                    {METRIC_COLUMNS.map((column) => (
                      <TableCell
                        key={column.key}
                        align="right"
                        sx={{
                          ...BODY_CELL_SX,
                          borderBottom:
                            rowIndex === 4 ? "none" : "1px solid #E0E0E0",
                        }}
                      >
                        <Skeleton width="60px" height={20} sx={{ ml: "auto" }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.length === 0
                ? (
                  <TableRow>
                    <TableCell
                      colSpan={1 + METRIC_COLUMNS.length}
                      align="center"
                      sx={{ ...BODY_CELL_SX, py: 4, borderBottom: "none" }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
                : rows.map((row, rowIndex) => (
                    <TableRow key={`${row?.name}-${row?.asin || ""}-${rowIndex}`} sx={{ height: "58px" }}>
                      <TableCell
                        sx={{
                          ...BODY_CELL_SX,
                          borderBottom:
                            rowIndex === rows.length - 1
                              ? "none"
                              : "1px solid #E0E0E0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          {showProductImage ? (
                            <img
                              src={row?.image || imageThumbnail}
                              alt=""
                              width={32}
                              height={32}
                              style={{
                                borderRadius: 2,
                                objectFit: "cover",
                                flexShrink: 0,
                                background: "#D9D9D9",
                              }}
                            />
                          ) : null}
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              lineHeight: 1.4,
                            }}
                          >
                            {row?.name || "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                      {METRIC_COLUMNS.map((column) => (
                        <TableCell
                          key={column.key}
                          align="right"
                          sx={{
                            ...BODY_CELL_SX,
                            borderBottom:
                              rowIndex === rows.length - 1
                                ? "none"
                                : "1px solid #E0E0E0",
                          }}
                        >
                          {formatMetric(column, row?.[column.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default function PerformanceSummary({ view = "both" } = {}) {
  const { categoryRows, productRows, loading } = useAmazonPerformanceSummary();
  const showCategory = view !== "product";
  const showProduct = view !== "category";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns:
          showCategory && showProduct ? { xs: "1fr", lg: "1fr 1fr" } : "1fr",
        gap: 2,
        mb: 4,
        alignItems: "stretch",
      }}
    >
      {showCategory ? (
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
              Category Performance
            </Typography>
            <SectionInfoIcon
              lines={[
                "Top 5 categories by Spends for the selected dates.",
                "Sponsored Brands campaigns (SB and SB Video) are excluded.",
                "Spends = advertising cost.",
                "Orders = 14-day attributed conversions.",
                "Revenue = 14-day attributed sales.",
                "ROAS = Revenue / Spends",
              ]}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Monitor performance insights to spot growth opportunities and
            optimize results.
          </Typography>
        </Box>
        <PerformanceTable
          firstColumnTitle="Category"
          rows={categoryRows}
          loading={loading}
          emptyMessage="No category data for the selected period"
        />
      </Box>
      ) : null}

      {showProduct ? (
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: "16px" }}>
              Product Performance
            </Typography>
            <SectionInfoIcon
              lines={[
                "Top 5 products (ASIN) by Spends for the selected dates.",
                "Sponsored Brands campaigns (SB and SB Video) are excluded.",
                "Spends = advertising cost.",
                "Orders = 14-day attributed conversions.",
                "Revenue = 14-day attributed sales.",
                "ROAS = Revenue / Spends",
              ]}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Monitor performance insights to spot growth opportunities and
            optimize results.
          </Typography>
        </Box>
        <PerformanceTable
          firstColumnTitle="Product"
          rows={productRows}
          loading={loading}
          showProductImage
          emptyMessage="No product data for the selected period"
        />
      </Box>
      ) : null}
    </Box>
  );
}
